/**
 * WeeklySetTargets — Fitbod-style weekly volume tracker
 *
 * Layout:
 *   1. Hexagon progress indicator — overall completion % (push + pull + legs)
 *   2. Color-coded sections — Push / Pull / Legs — each expandable
 *      Each muscle row shows current / target sets + a thin progress bar
 *   3. History row — past 4 complete weeks as mini hexagons
 */

import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { getMuscleWeeklyTarget, isHardSet } from '@/hooks/useWeeklyVolume';
import type { ExperienceLevel, MuscleGroup, TrainingGoal } from '@/types';

// ── Muscle Groups ─────────────────────────────────────────────

type GroupId = 'push' | 'pull' | 'legs';

const GROUP_CONFIG: Record<GroupId, {
  label: string;
  color: string;         // Tailwind text color
  bg: string;            // Tailwind bg color (faint)
  stroke: string;        // SVG stroke color
  muscles: MuscleGroup[];
}> = {
  push: {
    label: 'Push Muscles',
    color: 'text-warning',
    bg:    'bg-warning/10',
    stroke: '#f59e0b',
    muscles: ['chest', 'front_delt', 'lateral_delt', 'triceps'],
  },
  pull: {
    label: 'Pull Muscles',
    color: 'text-accent',
    bg:    'bg-accent/10',
    stroke: '#7c3aed',
    muscles: ['lats', 'rear_delt', 'biceps', 'traps', 'rhomboids'],
  },
  legs: {
    label: 'Leg Muscles',
    color: 'text-fresh',
    bg:    'bg-fresh/10',
    stroke: '#22c55e',
    muscles: ['quads', 'hamstrings', 'glutes', 'calves'],
  },
};

const MUSCLE_LABELS: Partial<Record<MuscleGroup, string>> = {
  chest:        'Chest',
  front_delt:   'Front Delts',
  lateral_delt: 'Lateral Delts',
  triceps:      'Triceps',
  lats:         'Lats',
  rear_delt:    'Rear Delts',
  biceps:       'Biceps',
  traps:        'Traps',
  rhomboids:    'Rhomboids',
  quads:        'Quadriceps',
  hamstrings:   'Hamstrings',
  glutes:       'Glutes',
  calves:       'Calves',
};

// ── Hexagon SVG ───────────────────────────────────────────────

// Regular hexagon: 6 equal sides, circumradius 44, centered at (50,50)
// Points (flat-top orientation, starting top-right going clockwise):
const HEX_R = 44;
const HEX_CX = 50;
const HEX_CY = 50;
function hexPoints(r = HEX_R) {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 30);
    return `${HEX_CX + r * Math.cos(angle)},${HEX_CY + r * Math.sin(angle)}`;
  }).join(' ');
}
const HEX_PERIMETER = 6 * HEX_R; // ≈ 264

interface HexProgressProps {
  /** 0–100 */
  pct: number;
  /** Per-group completion 0–100 */
  groups: { id: GroupId; pct: number }[];
  size?: number;
}

function HexProgress({ pct, size = 140 }: HexProgressProps) {
  const filled = (pct / 100) * HEX_PERIMETER;
  const gap = HEX_PERIMETER - filled;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      {/* Track */}
      <polygon
        points={hexPoints()}
        fill="none"
        stroke="var(--c-surface-300)"
        strokeWidth="6"
        strokeLinejoin="round"
      />
      {/* Fill — single arc representing overall % */}
      <polygon
        points={hexPoints()}
        fill="none"
        stroke="#7c3aed"
        strokeWidth="6"
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        style={{ transition: 'stroke-dasharray 0.8s ease-out' }}
      />
      {/* Center label */}
      <text
        x="50" y="46"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="18"
        fontWeight="700"
        fill="var(--c-ink)"
      >
        {pct}%
      </text>
      <text
        x="50" y="60"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="7"
        fill="var(--c-ink-muted)"
      >
        of target
      </text>
    </svg>
  );
}

// ── Mini history hexagon ──────────────────────────────────────

function MiniHex({ pct, label }: { pct: number; label: string }) {
  const filled = (pct / 100) * HEX_PERIMETER;
  const gap = HEX_PERIMETER - filled;
  const color = pct >= 90 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={56} height={56} viewBox="0 0 100 100">
        <polygon points={hexPoints()} fill="none" stroke="var(--c-surface-300)" strokeWidth="8" strokeLinejoin="round" />
        <polygon
          points={hexPoints()}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinejoin="round"
          strokeDasharray={`${filled} ${gap}`}
        />
        <text x="50" y="53" textAnchor="middle" dominantBaseline="middle" fontSize="19" fontWeight="700" fill="var(--c-ink)">
          {pct}%
        </text>
      </svg>
      <span className="text-xs text-ink-faint">{label}</span>
    </div>
  );
}

// ── Volume computation (pure, from loggedSets) ────────────────

async function computeWindowVolume(
  fromMs: number,
  toMs: number,
): Promise<Map<MuscleGroup, number>> {
  const from = new Date(fromMs).toISOString();
  const to   = new Date(toMs).toISOString();

  const sets = await db.loggedSets
    .where('loggedAt').between(from, to)
    .filter((s) => isHardSet(s.rpe, s.isWarmup))
    .toArray();

  const exIds = [...new Set(sets.map((s) => s.exerciseId))];
  const exercises = await db.exercises.bulkGet(exIds);
  const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

  const vol = new Map<MuscleGroup, number>();
  for (const s of sets) {
    const ex = exMap.get(s.exerciseId);
    if (!ex) continue;
    for (const alloc of ex.muscles) {
      vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + alloc.fraction);
    }
  }
  return vol;
}

function groupCompletion(
  volMap: Map<MuscleGroup, number>,
  muscles: MuscleGroup[],
  exp: ExperienceLevel,
  goal: TrainingGoal,
): { done: number; total: number } {
  let done = 0, total = 0;
  for (const m of muscles) {
    const target = getMuscleWeeklyTarget(m, exp, goal);
    done  += Math.min(volMap.get(m) ?? 0, target);
    total += target;
  }
  return { done, total };
}

function pctOf(done: number, total: number) {
  if (total === 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

// ── ISO week helpers ──────────────────────────────────────────

/** Returns [startMs, endMs] for the week n weeks ago (week starts Monday). */
function weekWindow(weeksAgo: number): [number, number] {
  const now = new Date();
  const dayOfWeek = (now.getDay() + 6) % 7; // 0=Mon…6=Sun
  const thisMondayMs = now.getTime() - dayOfWeek * 86_400_000;
  const startMs = thisMondayMs - weeksAgo * 7 * 86_400_000;
  return [startMs, startMs + 7 * 86_400_000];
}

function weekLabel(weeksAgo: number): string {
  const [startMs] = weekWindow(weeksAgo);
  return new Date(startMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Muscle row ────────────────────────────────────────────────

function MuscleRow({
  muscle,
  done,
  target,
  color,
}: {
  muscle: MuscleGroup;
  done: number;
  target: number;
  color: string; // tailwind text color for the badge
}) {
  const pct = pctOf(done, target);
  const complete = done >= target;

  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          {/* Completion badge */}
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs
                          ${complete ? `${color.replace('text-', 'bg-')} text-white` : 'bg-surface-300 text-ink-faint'}`}>
            {complete ? '✓' : ''}
          </div>
          <span className="text-sm text-ink">{MUSCLE_LABELS[muscle] ?? muscle}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-semibold ${complete ? color : 'text-ink'}`}>
            {Math.round(done * 10) / 10}
          </span>
          <span className="text-xs text-ink-faint">/ {target} sets</span>
          {!complete && (
            <span className={`text-xs ${color} ml-1`}>{target - Math.round(done)} to go</span>
          )}
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-surface-300 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color.replace('text-', 'bg-')}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Group section ─────────────────────────────────────────────

function GroupSection({
  id,
  volMap,
  exp,
  goal,
  defaultOpen = false,
}: {
  id: GroupId;
  volMap: Map<MuscleGroup, number>;
  exp: ExperienceLevel;
  goal: TrainingGoal;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const cfg = GROUP_CONFIG[id];
  const { done, total } = groupCompletion(volMap, cfg.muscles, exp, goal);
  const pct = pctOf(done, total);
  const complete = pct >= 100;

  return (
    <div className={`rounded-xl ${cfg.bg} overflow-hidden`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5 active:opacity-70 transition-opacity"
      >
        <div className="flex items-center gap-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                          ${complete ? `${cfg.bg} border-2 border-${cfg.stroke}` : cfg.bg}
                          ${cfg.color}`}>
            {complete ? '✓' : ''}
          </div>
          <div className="text-left">
            <p className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</p>
            <p className="text-xs text-ink-muted">
              {Math.round(done * 10) / 10} / {total} Sets
            </p>
          </div>
        </div>
        <span className="text-ink-faint text-sm">{open ? '∧' : '∨'}</span>
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-0 divide-y divide-surface-300/40">
          {cfg.muscles.map((m) => (
            <MuscleRow
              key={m}
              muscle={m}
              done={volMap.get(m) ?? 0}
              target={getMuscleWeeklyTarget(m, exp, goal)}
              color={cfg.color}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function WeeklySetTargets() {
  const [historyOpen, setHistoryOpen] = useState(false);

  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  const exp  = settings?.experienceLevel ?? 'intermediate';
  const goal = settings?.trainingGoal    ?? 'hypertrophy';

  // Current week's volume (rolling 7 days from today's Monday)
  const [startMs] = weekWindow(0);
  const currentVol = useLiveQuery(
    () => computeWindowVolume(startMs, Date.now()),
    [startMs],
  ) ?? new Map<MuscleGroup, number>();

  // Overall completion
  const allMuscles = (Object.values(GROUP_CONFIG) as typeof GROUP_CONFIG[GroupId][]).flatMap((g) => g.muscles);
  const { done: totalDone, total: grandTotal } = groupCompletion(currentVol, allMuscles, exp, goal);
  const overallPct = pctOf(totalDone, grandTotal);

  // Per-group summaries for the hexagon segments
  const groupPcts = (Object.keys(GROUP_CONFIG) as GroupId[]).map((id) => ({
    id,
    pct: pctOf(...Object.values(groupCompletion(currentVol, GROUP_CONFIG[id].muscles, exp, goal)) as [number, number]),
  }));

  // History: past 4 complete weeks
  const historyData = useLiveQuery(async () => {
    const results = [];
    for (let w = 4; w >= 1; w--) {
      const [s, e] = weekWindow(w);
      const vol = await computeWindowVolume(s, e);
      const { done, total } = groupCompletion(vol, allMuscles, exp, goal);
      results.push({ pct: pctOf(done, total), label: weekLabel(w) });
    }
    return results;
  }, [exp, goal]) ?? [];

  // Week range label
  const weekStart = new Date(startMs);
  const weekEnd   = new Date(startMs + 6 * 86_400_000);
  const weekRange = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <div className="card space-y-4 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-ink">Weekly Set Targets</h2>
        <p className="text-xs text-ink-muted">{weekRange}</p>
      </div>

      {/* Hexagon + group summary */}
      <div className="flex items-center gap-5">
        <HexProgress pct={overallPct} groups={groupPcts} />
        <div className="flex-1 space-y-2">
          {(Object.keys(GROUP_CONFIG) as GroupId[]).map((id) => {
            const cfg = GROUP_CONFIG[id];
            const { done, total } = groupCompletion(currentVol, cfg.muscles, exp, goal);
            const pct = pctOf(done, total);
            return (
              <div key={id}>
                <div className="flex justify-between items-center mb-0.5">
                  <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                  <span className="text-xs text-ink-muted">{Math.round(done * 10) / 10}/{total}</span>
                </div>
                <div className="h-1.5 bg-surface-300 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: cfg.stroke }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Muscle group sections */}
      <div className="space-y-2">
        {(Object.keys(GROUP_CONFIG) as GroupId[]).map((id) => (
          <GroupSection
            key={id}
            id={id}
            volMap={currentVol}
            exp={exp}
            goal={goal}
          />
        ))}
      </div>

      {/* History */}
      {historyData.length > 0 && (
        <div>
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center justify-between py-1 active:opacity-70"
          >
            <span className="text-sm font-semibold text-ink">History</span>
            <span className="text-ink-faint text-sm">{historyOpen ? '∧' : '∨'}</span>
          </button>
          {historyOpen && (
            <div className="flex justify-around pt-3 animate-fade-in">
              {historyData.map(({ pct, label }) => (
                <MiniHex key={label} pct={pct} label={label} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
